<script lang="ts">
import type { KbdProps, TabsEmits, TabsProps, TabsSlots, TabsItem as UTabsItem } from '#ui/types';

export interface TabsItem extends UTabsItem {
	kbds?: string[] | KbdProps[];
}
</script>

<script setup lang="ts" generic="T extends TabsItem">
import type { ComponentPublicInstance } from 'vue';
import type { OverflowTabItem } from '#layers/ui/composables/use-collapsible-tabs';
import { useForwardProps } from 'reka-ui';
import { tv } from 'tailwind-variants';
import { computed, ref, useTemplateRef } from 'vue';
import { DDropdownMenu, DPopover } from '#components';
import { get, omit } from '#layers/base/public';
import { useCollapsibleTabs } from '#layers/ui/composables/use-collapsible-tabs';
import Tabs from '#ui/components/Tabs.vue';

const {
	labelKey = 'label',
	content = true,
	overflow,
	minVisibleTabs = 1,
	items,
	modelValue,
	defaultValue,
	variant,
	size,
	ui: uiProp,
	...forwardedProps
} = defineProps<TabsProps<T> & {
	overflow?: 'collapse' | 'scroll';
	/** Fewest tabs to keep visible before the rest collapse into the trigger. Pass 0 to let every tab (even the active one) collapse. */
	minVisibleTabs?: number;
}>();
const emit = defineEmits<TabsEmits>();
const slots = defineSlots<Omit<TabsSlots<T>, 'list-trailing'> & {
	'list-trailing'?: (props: { hasOverflow: boolean }) => any;
	/** Custom overflow menu body. When provided, the overflow trigger opens a popover rendering this instead of the default dropdown. */
	'overflow'?: (props: { items: OverflowTabItem[]; count: number; select: (value: string | number) => void; close: () => void }) => any;
}>();

// eslint-disable-next-line vue/no-setup-props-reactivity-loss -- defaultValue seeds uncontrolled use once; later changes come from tab selection
const localValue = ref<string | number>(defaultValue ?? '0');
const activeValue = computed({
	get: () => modelValue ?? localValue.value,
	set: (value: string | number) => {
		localValue.value = value;
		emit('update:modelValue', value);
	},
});
const forwarded = useForwardProps((): TabsProps<T> => ({ ...forwardedProps, labelKey, content, items, variant, size, ui: uiProp }));
const ownSlotNames = ['default', 'list-trailing', 'overflow'] as const;

const tabsUi = tv({
	slots: {
		content: '',
		root: '',
		list: '',
		indicator: '',
		trigger: '',
	},
	variants: {
		content: {
			// Hide panels rather than unmount them, keeping trigger aria-controls references valid
			false: { content: 'hidden' },
		},
		overflow: {
			collapse: { root: 'min-w-0', list: 'max-w-full min-w-0 overflow-x-clip', trigger: 'shrink-0' },
			scroll: { root: 'min-w-0', list: 'scrollbar-hidden max-w-full min-w-0 overflow-x-auto', trigger: 'shrink-0' },
		},
		variant: {
			pill: '',
			link: '',
		},
		orientation: {
			horizontal: '',
			vertical: '',
		},
		activeInOverflow: {
			true: { indicator: 'opacity-0' },
		},
	},
	compoundVariants: [
		{
			// A scroll container clips at its padding box, which hides the underline where it overlaps the list's border-b.
			// Draw the rule as an inset shadow over a 1px bottom padding instead, so the underline sits on it as in the other modes.
			overflow: 'scroll',
			variant: 'link',
			orientation: 'horizontal',
			class: { list: 'border-b-0 pb-px shadow-[inset_0_-1px_0_var(--ui-border)]', indicator: 'bottom-0' },
		},
	],
	defaultVariants: {
		variant: 'pill',
		orientation: 'horizontal',
	},
});

/**
 * Matches the overflow button radius to pill tabs at each supported size.
 * Inactive (ghost) mirrors the tab trigger radius; active (outline) mirrors the
 * active indicator pill, which sits 1px tighter to nest concentrically.
 */
const overflowTriggerVariant = tv({
	// link tabs (and any non-pill fallback) use the default button radius.
	base: 'max-w-full min-w-0 rounded-md',
	variants: {
		variant: { pill: '', link: '' },
		size: { xs: '', sm: '', md: '', lg: '', xl: '' },
		active: { true: '', false: '' },
	},
	compoundVariants: [
		{ variant: 'pill', size: ['md', 'lg', 'xl'], active: false, class: 'rounded-lg' },
		{ variant: 'pill', size: ['md', 'lg', 'xl'], active: true, class: 'rounded-[calc(var(--ui-radius)*2-1px)]' },
		{ variant: 'pill', size: ['xs', 'sm'], active: true, class: 'rounded-[calc(var(--ui-radius)*1.5-1px)]' },
	],
	defaultVariants: {
		variant: 'pill',
		size: 'md',
		active: false,
	},
});

const tabsRef = useTemplateRef<ComponentPublicInstance>('tabs');
const overflowTriggerRef = useTemplateRef<HTMLElement>('overflowTrigger');
const listTrailingRef = useTemplateRef<HTMLElement>('listTrailing');

const {
	itemsWithOverflowUi,
	overflowCount,
	hasOverflow,
	hasVisibleTabs,
	triggerMaxWidth,
	activeInOverflow,
	activeOverflowItem,
	overflowItems,
	overflowDropdownItems,
} = useCollapsibleTabs<T>({
	tabsRef,
	overflowTriggerRef,
	listTrailingRef,
	items: () => items,
	modelValue: () => activeValue.value,
	labelKey: () => labelKey as string,
	enabled: () => overflow === 'collapse',
	minVisibleItems: () => minVisibleTabs,
	onSelect: (value) => {
		activeValue.value = value;
	},
});

const mergedUi = computed(() => {
	const ui = tabsUi({
		content,
		overflow,
		variant,
		orientation: forwardedProps.orientation,
		activeInOverflow: activeInOverflow.value,
	});

	return {
		...uiProp,
		content: ui.content({ class: uiProp?.content }),
		root: ui.root({ class: uiProp?.root }),
		list: ui.list({ class: uiProp?.list }),
		indicator: ui.indicator({ class: uiProp?.indicator }),
		trigger: ui.trigger({ class: uiProp?.trigger }),
	};
});

const overflowTriggerIcon = computed(() => activeOverflowItem.value?.icon ?? 'lucide:ellipsis');
const overflowTriggerLabel = computed(() => activeInOverflow.value && activeOverflowItem.value
	? get(activeOverflowItem.value, labelKey as string) as string
	: undefined);
const overflowTriggerBaseClass = computed(() => overflowTriggerVariant({ variant, size, active: activeInOverflow.value }));

// A custom #overflow body opts the trigger into a popover; otherwise the collapsed tabs stay a plain dropdown menu.
const hasOverflowContent = computed(() => !!slots.overflow);
const overflowOpen = ref(false);
const overflowWrapper = computed(() => hasOverflowContent.value ? DPopover : DDropdownMenu);
const overflowWrapperProps = computed(() => hasOverflowContent.value
	? {
			'open': overflowOpen.value,
			'onUpdate:open': (value: boolean) => {
				overflowOpen.value = value;
			},
			'content': { align: 'start' as const },
			'ui': { content: 'p-0' },
		}
	: {
			items: overflowDropdownItems.value,
			size,
		});

function selectOverflow(value: string | number) {
	activeValue.value = value;
	overflowOpen.value = false;
}

function closeOverflow() {
	overflowOpen.value = false;
}

const OVERFLOW_LABEL_MIN_WIDTH = 96;
const OVERFLOW_BADGE_MIN_WIDTH = 48;

// Once the trigger's allotted width can no longer hold a readable label, fall back to the icon alone.
const showOverflowLabel = computed(() => !!overflowTriggerLabel.value
	&& !(activeOverflowItem.value?.icon && triggerMaxWidth.value !== undefined && triggerMaxWidth.value < OVERFLOW_LABEL_MIN_WIDTH));

// The active view collapses into its own pill only once every tab is gone. While any tab is visible the trigger
// stays a neutral count + arrow; showing a variable-width active pill alongside tabs makes the split oscillate at boundary widths.
// A model value that matches no tab has nothing to show in the pill, so the trigger stays neutral then too.
const showActivePill = computed(() => !hasVisibleTabs.value && activeInOverflow.value);

// The active pill already represents the active view, so the badge counts the other hidden views; a neutral trigger counts them all.
const overflowBadgeCount = computed(() => showActivePill.value ? overflowCount.value - 1 : overflowCount.value);
// Drop the +N once the trigger's allotted width can't hold it beside the chevron; the arrow alone still opens the menu.
const showOverflowBadge = computed(() => overflowBadgeCount.value > 0
	&& !(triggerMaxWidth.value !== undefined && triggerMaxWidth.value < OVERFLOW_BADGE_MIN_WIDTH));

// Restyles the ghost button to read like an inactive tab: muted medium-weight text that darkens on hover, with no hover background.
const overflowTriggerClass = 'shrink-0 gap-1 ps-1.5 font-medium text-muted hover:bg-transparent hover:text-default active:bg-transparent active:text-default focus-visible:text-default aria-expanded:text-default';

const activePillClass = computed(() => showOverflowLabel.value
	? `${overflowTriggerBaseClass.value} max-w-48 px-3! ring-0`
	: `${overflowTriggerBaseClass.value} shrink-0 px-2.5 ring-0`);
</script>

<template>
	<Tabs
		ref="tabs"
		v-bind="forwarded"
		v-model="activeValue"
		:items="itemsWithOverflowUi"
		content
		:ui="mergedUi"
	>
		<template v-for="(_, name) in omit(slots, ownSlotNames)" #[name]="slotData">
			<!-- @vue-expect-error -->
			<slot :name v-bind="slotData" />
		</template>

		<template #default="slotData: any">
			<DTooltip :disabled="!slotData.item.kbds" :text="get(slotData.item, labelKey as string)" :kbds="slotData.item.kbds">
				<span>
					<slot v-bind="slotData">
						{{ get(slotData.item, labelKey as string) }}
					</slot>
				</span>
			</DTooltip>
		</template>

		<template v-if="overflow === 'collapse'" #list-trailing>
			<div v-if="hasOverflow" ref="overflowTrigger" class="flex min-w-0 items-center">
				<component :is="overflowWrapper" v-bind="overflowWrapperProps">
					<div class="flex min-w-0 items-center">
						<!-- Active view pill: shown only once every tab has collapsed, so it never competes with visible tabs. -->
						<DButton
							v-if="showActivePill"
							variant="outline"
							color="neutral"
							:size
							:icon="overflowTriggerIcon"
							:label="showOverflowLabel ? overflowTriggerLabel : undefined"
							:aria-label="showOverflowLabel ? undefined : overflowTriggerLabel"
							tabindex="-1"
							:ui="{
								base: activePillClass,
								label: 'truncate',
								leadingIcon: 'shrink-0',
							}"
						/>
						<!-- Count of the remaining hidden views plus the dropdown arrow: the overflow control that replaces the add button. -->
						<DButton
							variant="ghost"
							color="neutral"
							:size
							trailing-icon="lucide:chevron-down"
							:aria-label="$t('common.n_more', overflowCount)"
							:ui="{ base: overflowTriggerClass, trailingIcon: 'shrink-0' }"
						>
							<span v-if="showOverflowBadge" class="shrink-0">+{{ overflowBadgeCount }}</span>
						</DButton>
					</div>

					<template v-if="hasOverflowContent" #content>
						<slot name="overflow" :items="overflowItems" :count="overflowCount" :select="selectOverflow" :close="closeOverflow" />
					</template>
				</component>
			</div>

			<div v-if="!!slots['list-trailing']" ref="listTrailing" class="flex shrink-0 items-center">
				<slot name="list-trailing" :has-overflow />
			</div>
		</template>

		<template v-else #list-trailing>
			<slot name="list-trailing" :has-overflow="false" />
		</template>
	</Tabs>
</template>
